import { Apartment } from '../types';

/**
 * Safely converts any value (string, number, object with name/label/title, null, undefined)
 * into a safe string to prevent React "Objects are not valid as a React child" errors.
 */
export function safeString(val: any, fallback: string = ''): string {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if (typeof val.name === 'string' && val.name.trim()) return val.name.trim();
    if (typeof val.label === 'string' && val.label.trim()) return val.label.trim();
    if (typeof val.title === 'string' && val.title.trim()) return val.title.trim();
    if (typeof val.text === 'string' && val.text.trim()) return val.text.trim();
    if (typeof val.value === 'string' && val.value.trim()) return val.value.trim();
    if (val.id !== undefined && typeof val.name === 'string') return val.name.trim();
    if (val.id !== undefined) return String(val.id);
  }
  return fallback;
}

/**
 * Safely ensures an array of strings from any input.
 */
export function safeStringArray(arr: any, fallback: string[] = []): string[] {
  if (!arr) return fallback;
  if (Array.isArray(arr)) {
    return arr
      .map(item => safeString(item))
      .filter(item => item.length > 0);
  }
  if (typeof arr === 'string') {
    return arr
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return fallback;
}

/**
 * Sanitizes an Apartment object to guarantee that every field is of expected primitive type.
 */
export function sanitizeApartment(raw: any): Apartment {
  if (!raw || typeof raw !== 'object') {
    return {
      id: `apt-${Date.now()}`,
      title: 'Неозаглавен имот',
      type: 'sale',
      price: 0,
      area: 0,
      rooms: 1,
      bathrooms: 1,
      floor: '1',
      neighborhood: 'Варна',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&fit=crop&q=60',
      tags: ['ПРОДАЖБА'],
      description: ''
    };
  }

  const id = safeString(raw.id || raw.estateId, `apt-${Date.now()}`);
  let title = safeString(raw.title, 'Имот във Варна');
  const type: 'sale' | 'rent' = raw.type === 'rent' ? 'rent' : 'sale';
  const price = typeof raw.price === 'number' ? raw.price : (parseFloat(raw.price) || 0);
  const area = typeof raw.area === 'number' ? raw.area : (parseFloat(raw.area) || 0);
  let rooms = typeof raw.rooms === 'number' ? raw.rooms : (raw.rooms !== undefined && raw.rooms !== null && raw.rooms !== '' && !isNaN(Number(raw.rooms)) ? Number(raw.rooms) : 0);
  let bathrooms = typeof raw.bathrooms === 'number' ? raw.bathrooms : (raw.bathrooms !== undefined && raw.bathrooms !== null && raw.bathrooms !== '' && !isNaN(Number(raw.bathrooms)) ? Number(raw.bathrooms) : 0);
  let floor = safeString(raw.floor, '1');
  const neighborhood = safeString(raw.neighborhood, 'Варна');
  const city = safeString(raw.city, 'Варна');
  const region = safeString(raw.region, 'Варна');
  const address = safeString(raw.address, '');
  const image = safeString(raw.image, 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&fit=crop&q=60');
  const gallery = safeStringArray(raw.gallery, [image]);
  const tags = safeStringArray(raw.tags, [type === 'sale' ? 'ПРОДАЖБА' : 'НАЕМ']);
  const description = safeString(raw.description, '');
  const phone = safeString(raw.phone || raw.brokerPhone, '');
  const brokerName = safeString(raw.brokerName, '');
  const brokerPhone = safeString(raw.brokerPhone || raw.phone, '');
  const brokerEmail = safeString(raw.brokerEmail, '');
  const brokerImage = safeString(raw.brokerImage, '');
  let estateType = safeString(raw.estateType, '');

  const estateTypeLower = estateType.toLowerCase();

  const isPlot = estateTypeLower.includes('парцел') || 
                 estateTypeLower.includes('упи') || 
                 estateTypeLower.includes('земедел') || 
                 estateTypeLower.includes('поземлен') || 
                 estateTypeLower.includes('терен') || 
                 estateTypeLower.includes('нива') || 
                 estateTypeLower.includes('инвест') ||
                 raw.estate_type === 40 ||
                 raw.estate_type_id === 40 ||
                 raw.estate_type === 39 ||
                 raw.estate_type_id === 39;

  const isGarage = !isPlot && (
    estateTypeLower.includes('гараж') || 
    estateTypeLower.includes('паркомяст') || 
    raw.estate_type === 31 ||
    raw.estate_type_id === 31
  );

  if (isPlot) {
    estateType = 'Парцел';
    rooms = 0;
    bathrooms = 0;
    floor = 'Терен';
  } else if (isGarage) {
    estateType = 'Гараж / Паркомясто';
    rooms = 0;
    bathrooms = 0;
    floor = 'Партер';
  }

  const buildType = safeString(raw.buildType, '');
  const stageOfConstruction = safeString(raw.stageOfConstruction, '');
  const furnishing = safeString(raw.furnishing, '');
  const heating = safeString(raw.heating, '');
  const parking = safeString(raw.parking, '');
  const security = safeString(raw.security, '');
  const purpose = safeString(raw.purpose, '');
  const stateOfProperty = safeString(raw.stateOfProperty, '');
  const exterior = safeString(raw.exterior, '');
  const extras = safeStringArray(raw.extras, []);
  const totalFloors = typeof raw.totalFloors === 'number' ? raw.totalFloors : (parseInt(raw.totalFloors, 10) || undefined);
  const isActive = raw.isActive !== undefined ? Boolean(raw.isActive) : true;

  return {
    id,
    estateId: safeString(raw.estateId || raw.id),
    title,
    type,
    price,
    currency: safeString(raw.currency, 'EUR'),
    area,
    rooms,
    bathrooms,
    floor,
    totalFloors,
    neighborhood,
    city,
    region,
    address,
    location: neighborhood,
    image,
    gallery: gallery.length > 0 ? gallery : [image],
    tags: tags.length > 0 ? tags : [type === 'sale' ? 'ПРОДАЖБА' : 'НАЕМ'],
    description,
    phone,
    brokerName,
    brokerPhone,
    brokerEmail,
    brokerImage: brokerImage || undefined,
    isCustom: Boolean(raw.isCustom),
    syncedFromApi: Boolean(raw.syncedFromApi),
    estateType,
    buildType,
    stageOfConstruction,
    furnishing,
    heating,
    parking,
    security,
    purpose,
    stateOfProperty,
    exterior,
    extras,
    isActive,
    imageZoom: typeof raw.imageZoom === 'number' ? raw.imageZoom : undefined,
    imagePanX: typeof raw.imagePanX === 'number' ? raw.imagePanX : undefined,
    imagePanY: typeof raw.imagePanY === 'number' ? raw.imagePanY : undefined,
    lastSyncedAt: safeString(raw.lastSyncedAt)
  };
}

export function sanitizeApartments(list: any[]): Apartment[] {
  if (!Array.isArray(list)) return [];
  return list.map(sanitizeApartment);
}
